/** @jsx jsx */
import { jsx } from "frontend/src/utils/jsx"
import Img from "gatsby-image"
import { getFluidFromAsset } from "frontend/src/utils/gatsbyImageHelpers"
import { useParallax } from "frontend/src/utils/useParallax"
import { useRef } from "react"

function ListBenefits({ title, subTitle, ornamentLeft, ornamentRight, sections }) {
  const positionsArray = ["flex-start", "center", "flex-end"]
  const wrapperRef = useRef()
  const { offset } = useParallax(wrapperRef)
  return (
    <div
      sx={{
        pb: [8, 13],
        pt: 13,
        display: ["grid", "grid", "grid"],
        gridTemplateColumns: [
          "1fr 1fr",
          "1fr 1fr",
          "1fr minmax(25px, 200px) minmax(150px, 250px) 400px minmax(150px, 250px) minmax(25px, 200px) 1fr",
        ],
      }}
      ref={wrapperRef}
    >
      <div
        sx={{
          gridColumn: ["1 / span 2", "1 / span 2", "3 / span 3"],
          gridRow: "1 / span 1",
          ml: [5, 5, 0],
          mr: [5, 10, 0],
        }}
      >
        <header sx={{ mb: 12, counterReset: "count", textAlign: "center" }}>
          <h2
            sx={{
              fontFamily: "heading",
              color: "almostBlack",
              textShadow: "0 0 2px white",
              fontSize: ["28px", "28px", "38px"],
              lineHeight: ["30px", "30px", "44px"],
              fontWeight: ["medium", "medium", "regular"],
              letterSpacing: "-1.24px",
              mb: 5,
            }}
          >
            {title}
          </h2>
          <p
            sx={{
              fontFamily: "body",
              fontSize: [2, 2, "22px"],
              fontWeight: "light",
              letterSpacing: "0.24px",
              lineHeight: ["26px", "26px", "28px"],
              mb: 11,
              color: "almostBlack",
              textShadow: "0 0 2px white",
            }}
          >
            {subTitle}
          </p>
        </header>
        <section sx={{ display: "flex", flexDirection: "column", mb: [5, 0] }}>
          {sections.map((section, i) => (
            <article
              key={section._key}
              sx={{
                display: "flex",
                mb: i !== sections.length - 1 ? 11 : 0,
                alignItems: ["start", "stretch"],
                counterIncrement: "count",
                fontFamily: "heading",
                alignSelf: ["flex-start", positionsArray[i]],
                width: ["100%", "100%", "auto"],
                flexWrap: ["wrap", "wrap", "nowrap"],
              }}
            >
              <figure
                sx={{
                  pr: ["0.5em", ".125em", 7],
                  mr: [0, ".175em", 7],
                  fontSize: [2, 58],
                  lineHeight: [1.15, 1],
                  color: "green_400",
                  borderColor: "green_400",
                  borderRightWidth: 1,
                  borderRightStyle: "solid",
                  textShadow: "0 0 2px white",
                  "::after": { content: "counter(count, decimal-leading-zero)" },
                }}
              />
              <div sx={{ width: ["unset", "unset", 350] }}>
                <h2
                  sx={{
                    color: "almostBlack",
                    fontSize: [2, 3, "22px"],
                    lineHeight: [1.15, 3, "28px"],
                    mb: 2,
                    ml: [2, "unset"],
                    letterSpacing: "0px",
                    textShadow: "0 0 2px white",
                  }}
                >
                  {section.title}
                </h2>
                <p
                  sx={{
                    display: ["none", "block", "block"],
                    variant: "text.body1",
                    color: "almostBlack",
                    fontSize: [1, 2, "18px"],
                    lineHeight: ["unset", "unset", 1.4],
                    fontWeight: "light",
                    letterSpacing: "0px",
                    textShadow: "0 0 2px white",
                  }}
                >
                  {section.subTitle}
                </p>
              </div>
              <p
                sx={{
                  display: ["block", "none", "none"],
                  variant: "text.body1",
                  color: "almostBlack",
                  fontSize: [1, 2],
                  textShadow: "0 0 2px white",
                  width: "100%",
                }}
              >
                {section.subTitle}
              </p>
            </article>
          ))}
        </section>
      </div>

      {ornamentLeft ? (
        <Img
          fluid={getFluidFromAsset(ornamentLeft, 400)}
          alt="monstera leaf and cherry"
          imgStyle={{ objectFit: "contain", objectPosition: "left center" }}
          sx={{
            display: ["none", "block"],
            height: ["600px", "", ""],
            gridRow: "1 / span 1",
            gridColumn: ["2 / span 1", "2 / span 1", "1 / span 3"],
            width: ["100%", "100%", "100%"],
            zIndex: 2,
            justifySelf: ["right", "right", "left"],
            transform: [
              `scaleX(-1) translate3d(0,${-100 + offset / 6}px,0)`,
              `scaleX(-1) translate3d(0,${-100 + offset / 6}px,0)`,
              `translate3d(0,${100 + offset / 4}px,0)`,
            ],
          }}
        />
      ) : (
        <div
          sx={{
            gridRow: "1 / span 1",
            gridColumn: ["2 / span 1", "2 / span 1", "1 / span 3"],
            zIndex: -1,
            height: ["600px", "", ""],
            width: "100%",
          }}
        />
      )}

      {ornamentRight ? (
        <Img
          fluid={getFluidFromAsset(ornamentRight, 400)}
          alt="monatera leaf"
          imgStyle={{ objectFit: "contain", objectPosition: "right center" }}
          sx={{
            height: "400px",
            gridColumn: "5 / span 3",
            gridRow: "1 / span 1",
            display: ["none", "none", "block"],
            zIndex: -1,
            transform: ["none", `translate3d(0,${300 - offset / 2.5}px,0)`],
          }}
        />
      ) : (
        <div
          sx={{
            gridRow: "1 / span 1",
            gridColumn: "5 / span 3",
            zIndex: -1,
            display: ["none", "none", "block"],
            height: ["600px", "", ""],
            width: "100%",
          }}
        />
      )}
    </div>
  )
}

export { ListBenefits }
